/**
 * Sentinel-2 imagery source definitions (Digital Earth Australia).
 *
 * The ONE place a DEA tile URL is built. Everything else (map sources, the time
 * bar, the age chip) goes through here so there is a single place to change when
 * the CDN proxy lands (M6) or the self-hosted mosaic replaces the live backdrop
 * (M6b).
 *
 * Source: Geoscience Australia, Digital Earth Australia OWS.
 *   - `ga_s2m_ard_3`   Sentinel-2 A/B/C Analysis Ready Data, ~3950 acquisition
 *                      dates from 2015-07-12, selected with the `Time` dimension.
 *   - `s2_barest_earth` A multi-year, cloud-free, seasonless composite. No date,
 *                      no staleness — used here as the low-zoom backdrop so the
 *                      map is never blank below the dated layer's zoom gate.
 * Licence: CC BY 4.0 — commercial use is permitted with the attribution below.
 *
 * See gold-finder/IMAGERY_TIME_SERIES_PLAN.md and
 * infrastructure/docs/imagery-topology.puml.
 */

// Swapped to '/imagery/live' once the Cloud CDN pull-through exists (M6).
// Kept in an env var so that is a deploy-time change, not a code change.
export const DEA_WMTS_BASE =
  process.env.REACT_APP_DEA_WMTS_BASE || 'https://ows.dea.ga.gov.au/wmts';

export const DEA_ATTRIBUTION =
  '© Commonwealth of Australia (Geoscience Australia) — ' +
  '<a href="https://www.dea.ga.gov.au/" target="_blank" rel="noreferrer">Digital Earth Australia</a>. ' +
  'Contains modified Copernicus Sentinel data.';

export const SENTINEL_LAYER = 'ga_s2m_ard_3';
export const LANDSAT_LAYER = 'ga_ls_ard_3';

/**
 * Sensors the clearing scan can run on.
 *
 * Landsat buys 29 extra years at the cost of 9x the ground area per pixel: a
 * 30 m pixel is 900 m², so it will show a paddock-sized clearing and miss a
 * drill pad. The UI has to say so — a 1986 answer at 30 m is a different claim
 * from a 2015 answer at 10 m, and they must not look alike.
 *
 * Acquisition dates come from Earth Search and were checked to line up exactly
 * with DEA's own Time values for both layers.
 */
export const SCAN_SENSORS = {
  s2: {
    id: 's2',
    label: 'Sentinel-2',
    layer: SENTINEL_LAYER,
    collection: 'sentinel-2-l2a',
    resolution: '10 m',
    archiveStart: '2015-07-12',
    note: null,
    // The two layers do not share style ids, so each carries its own.
    styles: [
      { id: 'simple_rgb', label: 'True colour' },
      { id: 'infrared_green', label: 'False colour (SWIR/NIR/green)' },
      { id: 'ndvi', label: 'NDVI (vegetation)' },
    ],
  },
  landsat: {
    id: 'landsat',
    label: 'Landsat',
    layer: LANDSAT_LAYER,
    collection: 'landsat-c2-l2',
    resolution: '30 m',
    archiveStart: '1986-08-16',
    note: '30 m pixels — paddock scale, a drill pad will not show',
    styles: [
      { id: 'true_colour', label: 'True colour' },
      { id: 'false_colour', label: 'False colour' },
      { id: 'ndvi', label: 'NDVI (vegetation)' },
    ],
  },
};

/** Fall back to the sensor's first style when switching sensors mid-session. */
export function styleForSensor(sensorId, styleId) {
  const sensor = SCAN_SENSORS[sensorId] || SCAN_SENSORS.s2;
  return sensor.styles.some(s => s.id === styleId) ? styleId : sensor.styles[0].id;
}

/**
 * Tiles for the dated base layer on whichever sensor is chosen. Sentinel-2 is
 * 10 m back to 2015; Landsat is 30 m back to 1986, which is the only way to see
 * the ground for the older half of the land-change events.
 */
export function buildDatedTiles(sensorId, date, styleId) {
  if (!date) return null;
  const sensor = SCAN_SENSORS[sensorId] || SCAN_SENSORS.s2;
  return [wmtsTemplate({
    layer: sensor.layer,
    style: styleForSensor(sensor.id, styleId),
    time: date,
  })];
}
export const BAREST_EARTH_LAYER = 's2_barest_earth';

/** Server-side styles. The time bar (M2) exposes these; M1 uses true colour. */
export const SENTINEL_STYLES = {
  trueColour: { id: 'simple_rgb', label: 'True colour' },
  falseColour: { id: 'infrared_green', label: 'False colour (SWIR/NIR/green)' },
  ndvi: { id: 'ndvi', label: 'NDVI (vegetation)' },
};

/**
 * Zoom window for the dated layer.
 *
 * min: below z9 a 10 m national mosaic is meaningless and expensive, so the
 *      barest-earth backdrop covers that range instead.
 * max: hard limit — DEA's `WholeWorld_WebMercator` tile matrix set stops at
 *      level 14 and returns "Invalid Tile Matrix" above it. That happens to be
 *      the right cut-off anyway: 10 m pixels have no detail past z14, so
 *      MapLibre overzooms from here and it looks identical.
 */
export const SENTINEL_MIN_ZOOM = 9;
export const SENTINEL_MAX_ZOOM = 14;

/**
 * Lowest zoom at which the barest-earth backdrop has real pixels.
 *
 * Below z8 DEA answers with an 859-byte tile of flat slate blue (44,104,144) —
 * a placeholder, not imagery, and it reads as a solid coloured plane covering
 * the map. So the backdrop is clipped to z8-9 and the street base shows through
 * below that. Our own mosaic (M6b) is cut from z0 and removes this gap.
 */
export const BACKDROP_MIN_ZOOM = 8;

function wmtsTemplate({ layer, style, time }) {
  const params = [
    'service=WMTS',
    'request=GetTile',
    'version=1.0.0',
    `layer=${encodeURIComponent(layer)}`,
    `style=${encodeURIComponent(style)}`,
    `format=${encodeURIComponent('image/png')}`,
    'TileMatrixSet=WholeWorld_WebMercator',
    'TileMatrix={z}',
    'TileRow={y}',
    'TileCol={x}',
  ];
  if (time) params.push(`Time=${encodeURIComponent(time)}`);
  return `${DEA_WMTS_BASE}?${params.join('&')}`;
}

/**
 * Tile template for one acquisition date.
 * @param {string} date  'YYYY-MM-DD' — a date the STAC catalogue reported for
 *                       this viewport. A date with no overpass here renders a
 *                       155-byte transparent PNG, which is why dates must come
 *                       from the catalogue and never be guessed.
 */
export function buildSentinelTiles(date, styleId = SENTINEL_STYLES.trueColour.id) {
  if (!date) return null;
  return [wmtsTemplate({ layer: SENTINEL_LAYER, style: styleId, time: date })];
}

/**
 * Tile template for the change layer: the same tile at two dates, differenced
 * in the browser (see diffProtocol.js). Returns null unless both dates are set
 * and actually different — differencing a date against itself is a blank tile
 * and a wasted pair of requests.
 */
export function buildDiffTiles(fromDate, toDate, styleId, threshold) {
  if (!fromDate || !toDate || fromDate === toDate) return null;
  const q = new URLSearchParams({
    z: '{z}', x: '{x}', y: '{y}',
    from: fromDate,
    to: toDate,
    style: styleId || SENTINEL_STYLES.trueColour.id,
    threshold: String(threshold ?? 24),
  });
  // URLSearchParams escapes the braces MapLibre needs to substitute.
  const query = q.toString().replace(/%7B/g, '{').replace(/%7D/g, '}');
  return [`s2diff://tile?${query}`];
}

/**
 * Tile template for the clearing scan: N dates of NDVI, differenced over time
 * to find where vegetation was removed and stayed removed (diffProtocol.js).
 * Needs at least four observations to support a before/after split.
 */
export function buildClearingTiles(dates, threshold, sensorId) {
  if (!dates || dates.length < 4) return null;
  const sensor = SCAN_SENSORS[sensorId] || SCAN_SENSORS.s2;
  const q = new URLSearchParams({
    z: '{z}', x: '{x}', y: '{y}',
    dates: dates.join(','),
    threshold: String(threshold ?? 0.14),
    layer: sensor.layer,
  });
  const query = q.toString().replace(/%7B/g, '{').replace(/%7D/g, '}').replace(/%2C/g, ',');
  return [`s2clear://tile?${query}`];
}

/**
 * Land Change — our own derived layer (infrastructure/landchange), published
 * from PostGIS through GeoServer. Served as WMS rather than WFS so the bbox
 * goes out in EPSG:3857 and the EPSG:4326 axis-order question never arises.
 */
export const LAND_CHANGE = {
  id: 'landChange',
  label: 'Detected changes 1991-2023',
  layer: 'ne:wa_land_change',
  wms: process.env.REACT_APP_LANDCHANGE_WMS || '/geoserver/ne/wms',
};

/**
 * Eras and colours, kept in step with the SLD on the server. Defined here too
 * so the sidebar legend cannot drift from what the map actually draws.
 */
export const LAND_CHANGE_ERAS = [
  { from: 1991, to: 1994, colour: '#3b56a5' },
  { from: 1995, to: 1999, colour: '#4a90b0' },
  { from: 2000, to: 2004, colour: '#74adc9' },
  { from: 2005, to: 2009, colour: '#abd9e9' },
  { from: 2010, to: 2014, colour: '#fee090' },
  { from: 2015, to: 2019, colour: '#fdae61' },
  { from: 2020, to: 2023, colour: '#d7301f' },
];

/**
 * Greening is OFF: the gain rows were removed from the layer on 2026-08-27.
 * Validated against DEA Land Cover with a within-pixel permutation test, loss
 * changes land on a land-cover reclassification 2.34x more often than other
 * years in the same pixel, but gain scored 0.93x -- chance. The greening
 * direction appears to be tracking regional rainfall rather than events, so it
 * is hidden until the detection is reworked. Everything needed to bring it back
 * is still here: flip this flag and reload the gain rows from out_run4.
 */
export const SHOW_GREENING = false;

/**
 * Greening eras. Deliberately a single green ramp rather than a continuation of
 * the blue-red scale, so direction reads before the legend does: a loss patch
 * with a later greening patch over it is regrowth, not a standing clearing.
 */
export const LAND_GREENING_ERAS = [
  { from: 1991, to: 1999, colour: '#c7e9c0' },
  { from: 2000, to: 2009, colour: '#74c476' },
  { from: 2010, to: 2019, colour: '#31a354' },
  { from: 2020, to: 2023, colour: '#006d2c' },
];

/** WMS tiles for the land-change polygons. */
export function buildLandChangeTiles() {
  return [
    `${LAND_CHANGE.wms}?service=WMS&request=GetMap&layers=${encodeURIComponent(LAND_CHANGE.layer)}` +
    '&styles=&format=image/png&transparent=true&version=1.1.1' +
    '&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}',
  ];
}

/**
 * GetFeatureInfo for a click. WMS wants the click as a pixel offset inside a
 * described map request, so the caller passes the map's current bbox and size
 * along with where in it the user clicked.
 */
export function buildLandChangeInfoUrl({ bbox3857, width, height, x, y }) {
  const params = new URLSearchParams({
    service: 'WMS', request: 'GetFeatureInfo', version: '1.1.1',
    layers: LAND_CHANGE.layer, query_layers: LAND_CHANGE.layer,
    styles: '', format: 'image/png', info_format: 'application/json',
    srs: 'EPSG:3857', bbox: bbox3857.join(','),
    width: String(Math.round(width)), height: String(Math.round(height)),
    x: String(Math.round(x)), y: String(Math.round(y)),
    feature_count: '5', buffer: '10',
  });
  return `${LAND_CHANGE.wms}?${params.toString()}`;
}

/** Single-band tile for any dated layer — the protocols build NDVI from these. */
export function buildBandTiles(layer, style, date) {
  return [wmtsTemplate({ layer, style, time: date })];
}

/** Tile template for the undated barest-earth backdrop. */
export function buildBarestEarthTiles() {
  return [wmtsTemplate({ layer: BAREST_EARTH_LAYER, style: 'simple_rgb' })];
}

/**
 * Additive DEA overlays — "Change & vegetation" (M4) and the interpretive
 * terrain rasters (M5). Same service, same attribution, same tile matrix set.
 *
 * `time`:
 *   'selected' — follows the time bar's date (only ga_s2m_ard_3 does this)
 *   undefined  — the product is a composite or annual summary; omit Time and
 *                DEA serves its default (latest) slice.
 *
 * A DATED product with Time omitted is a trap: ga_ls_fc_3 (per-scene fractional
 * cover) answers a 155-byte transparent tile for any spot the default date's
 * pass did not cover, so the annual percentile product is used instead — it is
 * also the more useful layer, being a stable baseline rather than one flyover.
 */
/** GeoMAD is published per calendar year; DEA currently exposes 2013-2025. */
export const GEOMAD_YEARS = Array.from({ length: 13 }, (_, i) => `${2013 + i}-01-01`);

export const DEA_OVERLAYS = [
  {
    id: 'deaNdvi',
    group: 'change',
    label: 'NDVI (selected date)',
    hint: 'Vegetation vigour on the date shown in the time bar',
    layer: SENTINEL_LAYER,
    style: 'ndvi',
    time: 'selected',
    minzoom: SENTINEL_MIN_ZOOM,
  },
  {
    id: 'deaFractionalCover',
    group: 'change',
    label: 'Fractional cover (annual)',
    hint: 'Green / non-green / bare as an RGB composite',
    layer: 'ga_ls_fc_pc_cyear_3',
    style: 'fc_rgb',
  },
  {
    id: 'deaBareGround',
    group: 'change',
    label: 'Bare ground (annual median)',
    hint: 'How exposed the ground normally is — the baseline for spotting change',
    layer: 'ga_ls_fc_pc_cyear_3',
    style: 'bare_ground_50',
  },
  {
    // The serious change product. Unlike the two-date compare, this is computed
    // server-side on raw reflectance across EVERY clear observation in the year,
    // so a single hazy scene cannot move it and a change that appeared and
    // reverted still registers. Landsat, so 30 m — coarser than the S2 imagery
    // but the statistics are real.
    id: 'deaChangeMads',
    group: 'change',
    label: 'Change — GeoMAD (annual)',
    hint: 'Spectral deviation across a whole year of observations: clearing, pads, tracks, pit growth',
    layer: 'ga_ls8cls9c_gm_cyear_3',
    style: 'tmad_rgb_sens',
    time: 'year',
    years: GEOMAD_YEARS,
    styleChoices: [
      // Arid-tuned stretch: for the goldfields this pulls far more out of the
      // same data than the standard one, so it is the default here.
      { id: 'tmad_rgb_sens', label: 'Desert-tuned' },
      { id: 'tmad_rgb_std', label: 'Standard' },
    ],
  },
  {
    id: 'deaBarestEarth',
    group: 'change',
    label: 'Barest Earth (Sentinel-2)',
    hint: 'Vegetation stripped away — the geologist\'s backdrop',
    layer: BAREST_EARTH_LAYER,
    style: 'simple_rgb',
  },
  {
    id: 'deaFerricIron',
    group: 'change',
    label: 'Ferric iron index',
    hint: 'Free iron-oxide proxy from Barest Earth — gossans, ironstone',
    layer: 'landsat_barest_earth',
    style: 'nd_ferric_iron',
  },
  {
    id: 'deaClayMica',
    group: 'change',
    label: 'Clay & mica index',
    hint: 'Clay/mica alteration proxy from Barest Earth',
    layer: 'landsat_barest_earth',
    style: 'nd_clay_mica',
  },
  {
    id: 'deaWater',
    group: 'change',
    label: 'Water frequency (annual)',
    hint: 'How often surface water is observed — playa, drainage, water access',
    layer: 'ga_ls_wo_fq_cyear_3',
    style: 'annual_wofs_frequency_blues_transparent_3',
  },
  {
    id: 'deaMstp',
    group: 'terrain',
    label: 'Topographic position (MSTP)',
    hint: 'Ridge / slope / valley position — landform and drainage at a glance',
    layer: 'multi_scale_topographic_position',
    style: 'mstp_rgb',
  },
  {
    id: 'deaWeathering',
    group: 'terrain',
    label: 'Weathering intensity',
    hint: 'Regolith weathering — sampling depth and transported-cover risk',
    layer: 'weathering_intensity',
    style: 'wii',
  },
];

/**
 * Tiles for one DEA_OVERLAYS entry.
 *
 * `time: 'selected'` follows the time bar's date; `time: 'year'` follows the
 * year picked for that layer; anything else is a composite and takes no Time at
 * all (passing none to a *dated* product silently serves blank tiles — see the
 * ga_ls_fc_3 note in the plan).
 */
export function buildDeaOverlayTiles(overlay, date, options = {}) {
  if (overlay.wms) {
    // WMS 1.1.1 with an EPSG:3857 bbox, matching the SLIP raster pattern
    // already used in this map.
    return [
      `${overlay.wms}?service=WMS&request=GetMap&layers=${encodeURIComponent(overlay.wmsLayer)}` +
      '&styles=&format=image/png&transparent=true&version=1.1.1' +
      '&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}',
    ];
  }
  let time = null;
  let style = overlay.style;
  if (overlay.time === 'selected') {
    if (!date) return null;
    time = date;
  } else if (overlay.time === 'year') {
    time = options.geomadYear || overlay.years?.[overlay.years.length - 1];
    style = options.geomadStyle || overlay.style;
  }
  return [wmtsTemplate({ layer: overlay.layer, style, time })];
}

/**
 * Terrain (M5). AWS Open Data terrain tiles, terrarium-encoded, which MapLibre
 * reads natively as a raster-dem. Drives the hillshade layer today; the same
 * source is what map.setTerrain() would use for 3D.
 */
export const TERRAIN_TILES = [
  'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
];
export const TERRAIN_ATTRIBUTION =
  'Elevation: <a href="https://registry.opendata.aws/terrain-tiles/" target="_blank" rel="noreferrer">AWS Terrain Tiles</a>';
export const TERRAIN_MAX_ZOOM = 14;
